import { useMutation, useQuery, UseQueryOptions } from '@tanstack/react-query';

import { ENDPOINTS } from '@/api/endpoints';
import { fetcher } from '@/api/fetcher';
import {
  DeleteSeriesError,
  DeleteSeriesRequest,
  DeleteSeriesResponse,
  SeriesQueryError,
  SeriesQueryRequest,
  SeriesQueryResponse,
  SeriesRetrieveError,
  SeriesRetrieveRequest,
  SeriesRetrieveResponse,
} from '@/api/model';
import { removeEmptyParams } from '@/api/utils';

const KEY = {
  GET_SERIES_QUERY: 'GET_SERIES_QUERY',
} as const;

const request = {
  getSeriesQuery: async (
    { payload: { params, studyId } }: SeriesQueryRequest,
    signal?: AbortSignal,
  ) => {
    const { data } = await fetcher.HI.get<SeriesQueryResponse>(
      `${ENDPOINTS.HI.DICOM.SERIES_QUERY}/${studyId}`,
      { params: removeEmptyParams(params), signal },
    );

    return data;
  },
  postDeleteSeries: async ({ payload: { studyId, ...payload } }: DeleteSeriesRequest) => {
    const { data } = await fetcher.HI.post<DeleteSeriesResponse>(ENDPOINTS.HI.DICOM.DELETE_SERIES, {
      ...payload,
      studyID: studyId,
    });

    return data;
  },
  postSeriesRetrieve: async ({ payload: { params } }: SeriesRetrieveRequest) => {
    const { data } = await fetcher.HI.post<SeriesRetrieveResponse>(
      ENDPOINTS.HI.DICOM.SERIES_RETRIEVE,
      { params: removeEmptyParams(params) },
    );

    return data;
  },
};

const query = {
  useSeriesQuery: (
    { payload }: SeriesQueryRequest,
    config?: Partial<UseQueryOptions<SeriesQueryResponse, SeriesQueryError>>,
  ) =>
    useQuery<SeriesQueryResponse, SeriesQueryError>({
      queryFn: ({ signal }) => request.getSeriesQuery({ payload }, signal),
      queryKey: [KEY.GET_SERIES_QUERY, payload.studyId],
      ...config,
    }),
};

const mutation = {
  usePostDeleteSeries: () =>
    useMutation<DeleteSeriesResponse, DeleteSeriesError, DeleteSeriesRequest>({
      mutationFn: request.postDeleteSeries,
    }),
  usePostSeriesRetrieve: () =>
    useMutation<SeriesRetrieveResponse, SeriesRetrieveError, SeriesRetrieveRequest>({
      mutationFn: request.postSeriesRetrieve,
    }),
};

export const dicom = {
  mutation,
  query,
  request,
};
